/**
 * 1.5.0 — web re-scoring after food edits (mobile services/liver/rescore.ts parity).
 *
 * The decision (unchanged / re-scored from the stored scoreContext / stale) is
 * the shared, line-identical `scoreFoodEdit` in liver/scoring.ts — the same code
 * the server scores with. This module maps it onto NormalizedAnalysis (fresh
 * scans and saved foods shown through AnalysisResult) and onto a diary entry's
 * StoredAnalysis + StoredFoodItem[] (EntryDetailModal), and builds what a web
 * log persists. Every rebuild replaces the score fields as a whole — score,
 * breakdown, risk, per-food safety / concern, alcohol list, context.
 *
 * Servings never re-score: the Liver Score is per serving.
 */

import {
  acceptScoreContext,
  riskToSafety,
  scoreFoodEdit,
  type FoodEditScoring,
  type RescoreFood,
  type RescoreResult,
  type ScoreContextRejection,
} from '@/liver/scoring'
import type { NormalizedAnalysis, NormalizedFood } from '@/log/normalize'
import type { Nutrients, SafetyLevel, StoredAnalysis, StoredFoodItem } from '@/types'
import { scaleNutrients, sumNutrients } from '@/lib/nutrients'

/** Basis / replay rejections: the stored score no longer lines up with its foods / totals. */
export function isMismatchRejection(reason: ScoreContextRejection | undefined): boolean {
  return reason === 'basis' || reason === 'replay'
}

/** Remove keys whose value is undefined (Firestore rejects undefined). */
function compact<T extends object>(o: T): T {
  const out = { ...o } as Record<string, unknown>
  for (const k of Object.keys(out)) if (out[k] === undefined) delete out[k]
  return out as T
}

/** Keep the score, flag it stale; on a mismatch the context goes too, so a later save can't launder it. */
function markViewStale(view: NormalizedAnalysis, mismatch: boolean): NormalizedAnalysis {
  if (view.score == null) return view
  const out: NormalizedAnalysis = { ...view, scoreStale: true }
  if (mismatch) delete out.scoreContext
  return out
}

/** The same for a stored analysis (mobile markScoreStale). */
export function markStoredScoreStale(analysis: StoredAnalysis, mismatch: boolean): StoredAnalysis {
  if (analysis.impactScore == null) return analysis
  const out: StoredAnalysis = { ...analysis, scoreStale: true }
  if (mismatch) delete out.scoreContext
  return out
}

export function normalizedFoodToRescore(f: NormalizedFood): RescoreFood {
  return {
    key: f.key,
    name: f.name,
    calories: f.nutrients.calories,
    addedSugarsG: f.nutrients.addedSugarsG,
    saturatedFatG: f.nutrients.saturatedFatG,
    sodiumMg: f.nutrients.sodiumMg,
    fiberG: f.nutrients.fiberG,
    proteinG: f.nutrients.proteinG,
  }
}

export function storedFoodToRescore(f: StoredFoodItem): RescoreFood {
  return {
    key: f.key,
    name: f.name,
    calories: f.nutrients?.calories,
    addedSugarsG: f.nutrients?.addedSugarsG,
    saturatedFatG: f.nutrients?.saturatedFatG,
    sodiumMg: f.nutrients?.sodiumMg,
    fiberG: f.nutrients?.fiberG,
    proteinG: f.nutrients?.proteinG,
  }
}

/** The score fields a re-score writes (mobile rescoredFields). */
function rescoredFields(r: RescoreResult, now: Date) {
  return {
    scoreBreakdown: r.scoreBreakdown,
    alcoholFoods: r.alcoholFoods.length ? r.alcoholFoods : undefined,
    scoreContext: r.scoreContext,
    scoreEditedAt: now.toISOString(),
    proseStale: true as const,
    scoreStale: undefined,
  }
}

// ── Pre-log: fresh scans + saved foods (NormalizedAnalysis) ─────────────────

/**
 * The analysis to show and log after food edits on the result screen.
 * `analysis.foods` are the foods as received (keys intact), `foods` the current
 * list; the parent totals are `analysis.totals` (per serving).
 */
export function rescoreNormalized(
  analysis: NormalizedAnalysis,
  foods: NormalizedFood[],
  now: Date = new Date(),
): { view: NormalizedAnalysis; outcome: FoodEditScoring } {
  const outcome = scoreFoodEdit({
    scoreContext: analysis.scoreContext,
    storedScore: analysis.score,
    originalFoods: analysis.foods.map(normalizedFoodToRescore),
    editedFoods: foods.map(normalizedFoodToRescore),
    parent: analysis.totals,
    servings: 1,
  })
  if (outcome.kind === 'unchanged') {
    const mismatch = !outcome.acceptance.ok && isMismatchRejection(outcome.acceptance.reason)
    return { view: mismatch ? markViewStale({ ...analysis, foods }, true) : { ...analysis, foods }, outcome }
  }
  if (outcome.kind === 'stale') return { view: markViewStale({ ...analysis, foods }, isMismatchRejection(outcome.reason)), outcome }
  const r = outcome.result
  const rebuilt: NormalizedFood[] = foods.map((f, i) => {
    const fr = r.foods[i]
    const next: NormalizedFood = { ...f, risk: fr.riskLevel, safety: fr.safetyRating }
    if (fr.primaryConcern) next.primaryConcern = fr.primaryConcern
    else delete next.primaryConcern
    return next
  })
  return {
    view: compact({
      ...analysis,
      foods: rebuilt,
      score: r.impactScore,
      risk: r.overallRisk,
      ...rescoredFields(r, now),
    }),
    outcome,
  }
}

/**
 * Whether the shown score carries "Score is from the original scan": only once
 * it was flagged stale — an edit it couldn't follow, or a saved food whose
 * totals no longer match its foods (mobile isStoredScoreStale). An unedited
 * scan or saved food from before 1.5.0 shows no caption.
 */
export function isViewScoreStale(view: NormalizedAnalysis): boolean {
  if (view.score == null) return false
  return view.scoreStale === true
}

/** The entry `analysis` a web log persists — mobile handleAddToDiary parity. */
export function storedAnalysisFromView(view: NormalizedAnalysis): StoredAnalysis {
  return {
    ...(view.score != null && { impactScore: view.score }),
    ...(view.verdict && { verdict: view.verdict }),
    ...(view.reasoning && { reasoning: view.reasoning }),
    ...(view.tip && { tip: view.tip }),
    ...(view.scoreBreakdown && { scoreBreakdown: view.scoreBreakdown }),
    // 1.5.0: the score-edit state travels with the entry (mobile storedScoreEditFields).
    ...(view.score != null && { overallRisk: view.risk }),
    ...(view.scoreContext && { scoreContext: view.scoreContext }),
    ...(view.alcoholFoods?.length && { alcoholFoods: view.alcoholFoods }),
    ...(view.proseStale === true && { proseStale: true }),
    ...(view.scoreStale === true && { scoreStale: true }),
    ...(view.scoreEditedAt && { scoreEditedAt: view.scoreEditedAt }),
    ...(view.smartSwaps.length && {
      smartSwaps: view.smartSwaps.map((sw) => ({
        original: sw.original,
        suggestion: sw.suggestion,
        impactReduction: sw.impactReduction || '',
        ...(sw.reason && { reason: sw.reason }),
        ...(sw.difficulty && { difficulty: sw.difficulty }),
      })),
    }),
  }
}

/** Entry-level safety for a view (the re-scored risk when there is one). */
export function viewSafety(view: NormalizedAnalysis): SafetyLevel {
  return riskToSafety(view.risk)
}

// ── Logged entries (StoredAnalysis + StoredFoodItem[]) ──────────────────────

export interface StoredEditResult {
  analysis: StoredAnalysis | undefined
  foods: StoredFoodItem[]
  /** Entry-level safety to persist (unchanged unless re-scored). */
  safetyLevel: SafetyLevel
  outcome: FoodEditScoring | null
}

/**
 * Apply a food edit to a logged entry (mobile rescoreStoredEdit parity).
 * `parent` / `servings` are the stored totals the original foods must add up to
 * (entry.nutrients + servingsConsumed). Servings changes never re-score.
 */
export function rescoreStoredEdit(input: {
  analysis: StoredAnalysis | undefined
  originalFoods: StoredFoodItem[]
  editedFoods: StoredFoodItem[]
  parent: Partial<Nutrients> | null | undefined
  servings: number
  safetyLevel: SafetyLevel
  now?: Date
}): StoredEditResult {
  const { analysis, editedFoods } = input
  const outcome = scoreFoodEdit({
    scoreContext: analysis?.scoreContext,
    storedScore: analysis?.impactScore,
    originalFoods: input.originalFoods.map(storedFoodToRescore),
    editedFoods: editedFoods.map(storedFoodToRescore),
    parent: input.parent,
    servings: input.servings,
  })
  if (outcome.kind === 'unchanged') {
    const reason = outcome.acceptance.ok ? undefined : outcome.acceptance.reason
    const mismatch = input.parent != null && isMismatchRejection(reason)
    const flagged = analysis && mismatch ? markStoredScoreStale(analysis, true) : analysis
    return { analysis: flagged, foods: editedFoods, safetyLevel: input.safetyLevel, outcome }
  }
  if (outcome.kind === 'stale') {
    const mismatch = input.parent != null && isMismatchRejection(outcome.reason)
    const flagged = analysis ? markStoredScoreStale(analysis, mismatch) : analysis
    return { analysis: flagged, foods: editedFoods, safetyLevel: input.safetyLevel, outcome }
  }
  const r = outcome.result
  const foods = editedFoods.map((f, i) => {
    const fr = r.foods[i]
    const next: StoredFoodItem = { ...f, safetyLevel: fr.safetyRating }
    if (fr.primaryConcern) next.primaryConcern = fr.primaryConcern
    else delete next.primaryConcern
    return next
  })
  return {
    analysis: compact({
      ...(analysis ?? {}),
      impactScore: r.impactScore,
      overallRisk: r.overallRisk,
      ...rescoredFields(r, input.now ?? new Date()),
    }),
    foods,
    safetyLevel: riskToSafety(r.overallRisk),
    outcome,
  }
}

/**
 * "Score is from the original scan" for a stored analysis (mobile
 * isStoredScoreStale): only once flagged stale — an edit it couldn't follow, or
 * totals that no longer match its foods. An unedited entry from before 1.5.0
 * shows no caption.
 */
export function isStoredScoreStale(analysis: StoredAnalysis | undefined): boolean {
  if (analysis?.impactScore == null) return false
  return analysis.scoreStale === true
}

/**
 * Entry → saved food: keep the RAW scoring basis (mobile keepScoringBasis).
 * Per-serving totals are entry totals ÷ servings, and at small servings that
 * amplifies the entry's rounding (606 kcal at 0.1 servings → 61 → 610), which
 * would fail basis validation forever. With a valid context the scored
 * per-serving totals come from the foods instead.
 */
export function keepScoringBasis<T extends { nutrients: Nutrients }>(
  entry: { nutrients: Nutrients; servingsConsumed?: number; foods?: StoredFoodItem[]; analysis?: StoredAnalysis },
  perServing: T,
): T {
  const foods = entry.foods
  if (!foods?.length) return perServing
  const accepted = acceptScoreContext({
    scoreContext: entry.analysis?.scoreContext,
    storedScore: entry.analysis?.impactScore,
    foods: foods.map(storedFoodToRescore),
    parent: entry.nutrients,
    servings: entry.servingsConsumed || 1,
  })
  if (!accepted.ok) return perServing
  const fromFoods = scaleNutrients(sumNutrients(foods.map((f) => f.nutrients)), 1)
  const nutrients: Nutrients = { ...perServing.nutrients }
  for (const k of ['calories', 'addedSugarsG', 'saturatedFatG', 'sodiumMg'] as const) {
    nutrients[k] = fromFoods[k] ?? 0
  }
  return { ...perServing, nutrients }
}
